/**
 * Renders building sprites as pixel-art 24x24 Sprites, keyed by BuildingKind.
 */

import { Container, Graphics, Sprite } from 'pixi.js';
import { GameClientWorldState } from './game_client_world_state';
import { BUILDING_SPRITES } from './building_sprite_definitions';
import { generatePixelTexture } from './pixel_sprite_texture_generator';

const TILE_SIZE = 32;

export class RenderBuildingSprites {
  private container: Container;
  private sprites: Map<number, Sprite> = new Map();
  private hpBarGfx: Graphics;
  private highlightGfx: Graphics;

  constructor(container: Container) {
    this.container = container;
    this.hpBarGfx = new Graphics();
    this.highlightGfx = new Graphics();
    this.container.addChild(this.hpBarGfx);
    this.container.addChild(this.highlightGfx);
  }

  syncHighlight(selectedId: number | null): void {
    this.highlightGfx.clear();
    if (selectedId === null) return;
    const s = this.sprites.get(selectedId);
    if (!s) return;
    this.highlightGfx.rect(s.x - 2, s.y - 2, s.width + 4, s.height + 4);
    this.highlightGfx.fill({ color: 0xffffff, alpha: 0.3 });
    this.highlightGfx.rect(s.x - 2, s.y - 2, s.width + 4, s.height + 4);
    this.highlightGfx.stroke({ color: 0xffffff, width: 2, alpha: 1 });
  }

  sync(worldState: GameClientWorldState): void {
    const currentIds = new Set(worldState.buildings.keys());

    // Remove stale
    for (const [id, s] of this.sprites) {
      if (!currentIds.has(id)) {
        this.container.removeChild(s);
        s.destroy();
        this.sprites.delete(id);
      }
    }

    this.hpBarGfx.clear();

    // Add/update
    for (const b of worldState.buildings.values()) {
      let s = this.sprites.get(b.id);
      if (!s) {
        const texture = generatePixelTexture(BUILDING_SPRITES[b.kind], 8);
        s = new Sprite(texture);
        this.container.addChild(s);
        this.sprites.set(b.id, s);
      }

      s.x = b.position.x - 12;
      s.y = b.position.y - 12;

      // HP bar if damaged
      if (b.hp < b.max_hp) {
        const barW = TILE_SIZE;
        const barH = 4;
        const barY = b.position.y - TILE_SIZE / 2 - 6;
        const barX = b.position.x - barW / 2;
        const hpRatio = Math.max(0, b.hp / b.max_hp);
        this.hpBarGfx.rect(barX, barY, barW, barH);
        this.hpBarGfx.fill(0x333333);
        this.hpBarGfx.rect(barX, barY, barW * hpRatio, barH);
        this.hpBarGfx.fill(hpRatio > 0.5 ? 0x00ff00 : hpRatio > 0.25 ? 0xffff00 : 0xff0000);
      }
    }

    // Ensure HP bars and highlight render on top of sprites
    this.container.removeChild(this.hpBarGfx);
    this.container.removeChild(this.highlightGfx);
    this.container.addChild(this.hpBarGfx);
    this.container.addChild(this.highlightGfx);
  }
}
